import _ from 'underscore';
import { biddingStrategyTypeId, CampaignTypes } from '@bingads-webui-campaign-react/constants';
import {
  BudgetSuggestionTiers,
  ApiKeysBudgetSuggestion,
  BudgetDetailKeys,
  formatNumber,
  CampaignTypeToAdvertiserChannelTypeId,
  API_VERSION,
} from './constants';

const parseBudgetDetails = (data, i18n, currency) => {
  const BudgetDetails = _.mapObject(BudgetDetailKeys, (key) => {
    const detail = {
      Low: data[`${key}Low`],
      Mid: data[`${key}Mid`],
      High: data[`${key}High`],
      Range: '',
    };

    if (key === BudgetDetailKeys.CostPerConversion || key === BudgetDetailKeys.CostPerClick) {
      detail.Low = data[`${key}Low`];
      detail.Mid = data[`${key}Mid`];
      detail.High = data[`${key}High`];

      if (data[`${key}Low`] === 'INF' || data[`${key}High`] === 'INF') {
        detail.Range = '-';
      } else {
        detail.Range = `${i18n.formatCurrency(Math.round((data[`${key}Low`]) * 100) / 100, currency)} - ${i18n.formatCurrency(Math.round((data[`${key}High`]) * 100) / 100, currency)}`;
      }
    } else if (key === BudgetDetailKeys.CTR) {
      detail.Low = data[`${key}LowPercent`];
      detail.Mid = data[`${key}MidPercent`];
      detail.High = data[`${key}HighPercent`];
      detail.Range = `${Math.round(detail.Low * 1000) / 1000}% - ${Math.round(detail.High * 1000) / 1000}%`;
    } else {
      detail.Range = `${formatNumber(detail.Low, i18n)} - ${formatNumber(detail.High, i18n)}`;
    }

    return detail;
  });

  return BudgetDetails;
};

export const parseBudgetSuggestionAPI = (data, i18n, currency) => {
  // three budget suggestion tiers
  // 1. Advanced (high) 2. Balanced (medium) 3. Starter (low)

  const suffix = 'Details';

  const budgetSuggestions = {};

  _.each(BudgetSuggestionTiers, (tier, key) => {
    const budgetKey = `Budget${ApiKeysBudgetSuggestion[key]}30Day`;
    const budgetDailyKey = `Budget${ApiKeysBudgetSuggestion[key]}Daily`;
    const budgetDetailsKey = `Budget${ApiKeysBudgetSuggestion[key]}${suffix}`;

    const BudgetDetails = parseBudgetDetails(data[budgetDetailsKey], i18n, currency);

    budgetSuggestions[tier] = {
      BudgetAmountMonthly: data[budgetKey],
      BudgetAmountDaily: data[budgetDailyKey] ? Math.round(data[budgetDailyKey] * 100) / 100 : Math.round(data[budgetKey] / 30.43),
      BudgetDetails: { ...BudgetDetails },
    };
  });
  return budgetSuggestions;
};

export const getSuggestedBudgetAmounts = parsedBudgetSuggestionResponse =>
  _.map(parsedBudgetSuggestionResponse, value => value.BudgetAmountMonthly);

export const parsePerformanceEstimateAPI = (data, i18n, currency) => {
  const performanceEstimate = {};

  performanceEstimate.BudgetAmountMonthly = data.BudgetAdvertiser30Day;
  performanceEstimate.BudgetDetails = parseBudgetDetails(data.AdvertiserBudgetDetails, i18n, currency);

  return performanceEstimate;
};

export const parseBudgetLandscapeAPI = (data, i18n, currency) => {
  const result = {};

  if (data && data.value && data.value.length === 0) {
    return result;
  }

  const budgetLandscapeData = data.value && data.value[0];

  result.Current = {
    BudgetAmountMonthly: budgetLandscapeData.BudgetCurrent,
    BudgetAmountDaily: budgetLandscapeData.BudgetCurrentDaily ? Math.round(budgetLandscapeData.BudgetCurrentDaily * 100) / 100 : Math.round(budgetLandscapeData.BudgetCurrent / 30.43),
    BudgetDetails: { ...parseBudgetDetails(budgetLandscapeData.BudgetCurrentDetails, i18n, currency) },
  };
  result.Suggested = {

    BudgetAmountMonthly: budgetLandscapeData.BudgetSuggested,
    BudgetAmountDaily: budgetLandscapeData.BudgetSuggestedDaily ? Math.round(budgetLandscapeData.BudgetSuggestedDaily * 100) / 100 : Math.round(budgetLandscapeData.BudgetSuggested / 30.43),
    BudgetDetails: { ...parseBudgetDetails(budgetLandscapeData.BudgetSuggestedDetails, i18n, currency) },
  };
  result.OpportunityId = budgetLandscapeData?.OpportunityId || '';
  return result;
};

export const parseViewModelToMTModelForPMax = ({
  campaign,
  additionalData,
  currency,
  latestSuggestedBudget,
  isPerformanceEstimation,
  apiVersion = API_VERSION.V2,
  isExpansionPilotEnabled = true,
}) => {
  const {
    Budget,
    BiddingScheme,
    CampaignType,
    Languages,
    conversionGoals,
    locationTargets,
    Id: campaignId,
  } = campaign || {};

  const {
    BusinessName,
    Descriptions,
    Headlines,
    LongHeadlines,
    Id: assetGroupId,
    ProductListingGroups,
    AudienceGroup,
  } = additionalData || {};

  const {
    Dimensions,
  } = AudienceGroup || [];

  const {
    Amount: CurrentBudgetAmount,
  } = Budget || {};

  const {
    Type,
    TargetCpa,
    TargetRoas,
    TargetImpressionShare,
    MaxCpc,
  } = BiddingScheme || {};

  const {
    Amount: MaxCpcAmount,
  } = MaxCpc || {};

  const { goalSelection, goalScopeSelection } = conversionGoals || [];

  const { currentLevelSettings } = locationTargets || {};

  const { currentLocations } = currentLevelSettings || [];

  const LocationTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Location'), location => location.Criterion.LocationCriterion.LocationId);

  const RadiusTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Radius'), location => ({
    Radius: location.Criterion.RadiusCriterion.Radius,
    Latitude: location.Criterion.RadiusCriterion.Latitude,
    Longitude: location.Criterion.RadiusCriterion.Longitude,
  }));

  const BiddingSchemeId = biddingStrategyTypeId[Type] || biddingStrategyTypeId.MaxConversions;

  const GoalCategory = goalScopeSelection === 'campaign-scope' ? _.map(goalSelection, goal => goal.GoalCategory) : [];

  const ListingGroupId = ProductListingGroups && ProductListingGroups[0] && ProductListingGroups[0].Id;

  // isAudienceSignal is currently calcuated if the asset group has any inMarket Audiences.
  const audienceDimensions = _.find(Dimensions, item => item['@odata.type'] === '#Model.AudienceDimension') || {};
  const hasInMarketAudiences = _.any(audienceDimensions.Audiences || [], item => item.Type === 'InMarket');

  // filter operand
  const operand = ProductListingGroups && ProductListingGroups[1] ? ProductListingGroups[1].Condition.Operand : null;


  const data = {
    CampaignTypeId: CampaignTypeToAdvertiserChannelTypeId[CampaignType],
    BiddingSchemeId,
    TargetCpa: TargetCpa || null,
    MaxCpc: MaxCpcAmount || null,
    Languages,
    BusinessName: [BusinessName],
    Descriptions: _.values(Descriptions),
    Headlines: _.values(Headlines),
    LongHeadlines: _.values(LongHeadlines),
    GoalCategory,
    RadiusTargets,
    LocationIds: LocationTargets,
    CurrencyCode: currency,
    GetBudget: isPerformanceEstimation ? 0 : 1,
    RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : null,
    AdvertiserBudget: isPerformanceEstimation ? Math.round(CurrentBudgetAmount * 30.43) : null,
    TargetRoas: TargetRoas || null,
    TargetImpressionShare: TargetImpressionShare || null,
  };

  if (apiVersion === API_VERSION.V2) {
    if (campaignId == null) {
      throw new Error('CampaignId is required for V2 budget suggestion');
    }
    return JSON.stringify({
      CampaignId: `${campaignId}`,
      ...(assetGroupId != null && { AssetGroupId: `${assetGroupId}` }),
      ...(ListingGroupId != null && { ListingGroupId: `${ListingGroupId}` }),
      Data: {
        ...data,
        GetBudget: !isPerformanceEstimation,
        RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : [],
        FilterOperand: operand,
        IsAudienceSignalAdded: hasInMarketAudiences,
        IsExpansionPilotEnabled: !!isExpansionPilotEnabled,
      },
    });
  }

  // v1 version requires these two fields as empty always
  return {
    ...data,
    Age: [],
    Keywords: [],
  };
};

export const parseViewModelToMTModelForSearch = ({
  campaign,
  additionalData,
  currency,
  latestSuggestedBudget,
  isPerformanceEstimation,
  apiVersion = API_VERSION.V2,
  isExpansionPilotEnabled = true,
}) => {
  const {
    Budget,
    BiddingScheme,
    Languages,
    conversionGoals,
    locationTargets,
    Id: campaignId,
  } = campaign || {};

  const { keywords = [], adGroupId, previewAd } = additionalData || {};

  const {
    Headlines,
    Descriptions,
  } = previewAd || {};

  const {
    Amount: CurrentBudgetAmount,
  } = Budget || {};

  const {
    Type,
    TargetCpa,
    TargetRoas,
    TargetImpressionShare,
    MaxCpc,
  } = BiddingScheme || {};

  const {
    Amount: MaxCpcAmount,
  } = MaxCpc || {};

  const { goalSelection, goalScopeSelection } = conversionGoals || [];

  const { currentLevelSettings } = locationTargets || {};

  const { currentLocations } = currentLevelSettings || [];

  const LocationTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Location'), location => location.Criterion.LocationCriterion.LocationId);

  const RadiusTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Radius'), location => ({
    Radius: location.Criterion.RadiusCriterion.Radius,
    Latitude: location.Criterion.RadiusCriterion.Latitude,
    Longitude: location.Criterion.RadiusCriterion.Longitude,
  }));

  const BiddingSchemeId = biddingStrategyTypeId[Type] || biddingStrategyTypeId.MaxConversions;

  const GoalCategory = goalScopeSelection === 'campaign-scope' ? _.map(goalSelection, goal => goal.GoalCategory) : [];

  const data = {
    CampaignTypeId: CampaignTypeToAdvertiserChannelTypeId[CampaignTypes.Default],
    BiddingSchemeId,
    TargetCpa: TargetCpa || null,
    MaxCpc: MaxCpcAmount || null,
    Languages,
    Headlines: Headlines ? _.map(Headlines, h => (h.Asset ? h.Asset.Text : h)) : [],
    Descriptions: Descriptions ? _.map(Descriptions, d => (d.Asset ? d.Asset.Text : d)) : [],
    GoalCategory,
    RadiusTargets,
    LocationIds: LocationTargets,
    CurrencyCode: currency,
    GetBudget: isPerformanceEstimation ? 0 : 1,
    RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : null,
    AdvertiserBudget: isPerformanceEstimation ? Math.round(CurrentBudgetAmount * 30.43) : null,
    TargetRoas: TargetRoas || null,
    TargetImpressionShare: TargetImpressionShare || null,
  };

  if (apiVersion === API_VERSION.V2) {
    if (campaignId == null) {
      throw new Error('CampaignId is required for V2 budget suggestion');
    }
    return JSON.stringify({
      CampaignId: `${campaignId}`,
      ...(adGroupId != null && { AdGroupId: `${adGroupId}` }),
      Data: {
        ...data,
        Keywords: keywords,
        GetBudget: !isPerformanceEstimation,
        RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : [],
        IsExpansionPilotEnabled: !!isExpansionPilotEnabled,
      },
    });
  }

  return {
    ...data,
    Age: [],
    Keywords: [],
  };
};

export const parseViewModelToMTModelForShopping = ({
  campaign,
  additionalData,
  currency,
  latestSuggestedBudget,
  isPerformanceEstimation,
  apiVersion = API_VERSION.V2,
  isExpansionPilotEnabled = true,
}) => {
  const {
    Budget,
    BiddingScheme,
    Languages,
    conversionGoals,
    locationTargets,
    Id: campaignId,
  } = campaign || {};

  const { productPartitions, adGroupId } = additionalData || {};

  const { Id: ProductGroupId, ProductCondition: operand } = productPartitions || {};

  const {
    Amount: CurrentBudgetAmount,
  } = Budget || {};

  const {
    Type,
    TargetCpa,
    TargetRoas,
    TargetImpressionShare,
    MaxCpc,
  } = BiddingScheme || {};

  const {
    Amount: MaxCpcAmount,
  } = MaxCpc || {};

  const { goalSelection, goalScopeSelection } = conversionGoals || [];

  const { currentLevelSettings } = locationTargets || {};

  const { currentLocations } = currentLevelSettings || [];

  const LocationTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Location'), location => location.Criterion.LocationCriterion.LocationId);

  const RadiusTargets = _.map(_.filter(currentLocations, location => location.CriterionType === 'Radius'), location => ({
    Radius: location.Criterion.RadiusCriterion.Radius,
    Latitude: location.Criterion.RadiusCriterion.Latitude,
    Longitude: location.Criterion.RadiusCriterion.Longitude,
  }));

  const BiddingSchemeId = biddingStrategyTypeId[Type] || biddingStrategyTypeId.MaxConversions;

  const GoalCategory = goalScopeSelection === 'campaign-scope' ? _.map(goalSelection, goal => goal.GoalCategory) : [];

  const data = {
    CampaignTypeId: CampaignTypeToAdvertiserChannelTypeId[CampaignTypes.Shopping],
    BiddingSchemeId,
    TargetCpa: TargetCpa || null,
    MaxCpc: MaxCpcAmount || null,
    Languages,
    GoalCategory,
    RadiusTargets,
    LocationIds: LocationTargets,
    CurrencyCode: currency,
    GetBudget: isPerformanceEstimation ? 0 : 1,
    RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : null,
    AdvertiserBudget: isPerformanceEstimation ? Math.round(CurrentBudgetAmount * 30.43) : null,
    TargetRoas: TargetRoas || null,
    TargetImpressionShare: TargetImpressionShare || null,
  };

  if (apiVersion === API_VERSION.V2) {
    if (campaignId == null) {
      throw new Error('CampaignId is required for V2 budget suggestion');
    }
    return JSON.stringify({
      CampaignId: `${campaignId}`,
      ...(ProductGroupId != null && { ProductGroupId: `${ProductGroupId}` }),
      ...(adGroupId != null && { AdGroupId: `${adGroupId}` }),
      Data: {
        ...data,
        FilterOperand: operand,
        GetBudget: !isPerformanceEstimation,
        RecommendedBudget: isPerformanceEstimation ? latestSuggestedBudget : [],
        IsExpansionPilotEnabled: !!isExpansionPilotEnabled,
      },
    });
  }

  return {
    ...data,
    Age: [],
    Keywords: [],
  };
};
